import { parseChord } from './chord.js';

/**
 * The shortcut registry: every keyboard-reachable command in the editor, its
 * default keys, the context it applies in, and the user's rebinding on top.
 *
 * Contexts form a tree written with dots: `global` is the root, a workspace
 * view (`scene`, `model`, `code`, ...) is a child of it, and a sub-mode such as
 * `model.rig` is a child of its view. A binding in a deeper context wins over
 * the same chord in an ancestor (Q is the Select tool in the Scene view and
 * Quick Favorites elsewhere); the same chord twice in one context is a
 * conflict, which the Shortcut Manager reports.
 *
 * Keys resolve in three layers: the command's own defaults, then the active
 * keymap profile (Default / Blender-style / Unity-style / a custom one), then
 * the user's overrides recorded for that profile. Only the last layer is ever
 * written, so "reset" is just removing an override.
 *
 * This module is pure (no DOM): storage is injected, so it is testable in Node.
 */

export interface CommandSpec {
  /** Stable dotted id (`view.tab.scene`, `scene.duplicate`). Profiles and favorites key on it. */
  id: string;
  label: string;
  /** Grouping in the Shortcut Manager and search ("Window", "Edit", "Scene"). */
  category?: string;
  /** Where the binding applies; defaults to `global`. */
  context?: string;
  /** Default chords, e.g. `['Ctrl+Z']`. */
  keys?: readonly string[];
  run?: () => void | Promise<void>;
  /** Checked before running; a disabled command lets the key fall through. */
  enabled?: () => boolean;
  /** Icon name from ui/icons (kept a string here so this module stays UI-free). */
  icon?: string;
  /** Extra words search should match. */
  keywords?: string;
  /** Fire even while a text field has focus (app-level chords such as Ctrl+P). */
  whileTyping?: boolean;
  /** Let held-key auto-repeat fire it again. */
  repeat?: boolean;
  /**
   * Bound by a component's own key handler, not by the registry: listed and
   * conflict-checked, never dispatched or rebindable. Lets the manager show the
   * whole keymap while views migrate onto the registry.
   */
  external?: boolean;
  /** Keep out of the command palette / search (still bindable). */
  hidden?: boolean;
}

export interface Command extends CommandSpec {
  context: string;
  keys: readonly string[];
}

export interface KeymapProfile {
  id: string;
  name: string;
  builtin: boolean;
  /** Profile this one derives from (custom profiles). */
  base?: string;
  /** Command id -> chords, replacing the command defaults. */
  bindings: Readonly<Record<string, readonly string[]>>;
  description?: string;
}

export interface ShortcutPrefs {
  version: 1;
  profile: string;
  /** Per-profile user overrides: profile id -> command id -> chords. */
  layers: Record<string, Record<string, string[]>>;
  custom: Array<{ id: string; name: string; base: string }>;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface Conflict {
  chord: string;
  context: string;
  ids: string[];
}

export interface Shadow {
  chord: string;
  /** The command that wins (deeper context). */
  winner: string;
  /** The command it hides in that context. */
  hidden: string;
  context: string;
}

export const STORAGE_KEY = 'ai-studio:shortcuts';
export const GLOBAL = 'global';

/** True when `ancestor` contains `context` (or is it). */
export function contextIncludes(ancestor: string, context: string): boolean {
  return ancestor === GLOBAL || ancestor === context || context.startsWith(`${ancestor}.`);
}

/** Depth for ordering: global 0, `scene` 1, `model.rig` 2. */
export function contextDepth(context: string): number {
  return context === GLOBAL ? 0 : context.split('.').length;
}

function emptyPrefs(): ShortcutPrefs {
  return { version: 1, profile: 'default', layers: {}, custom: [] };
}

function canonical(keys: readonly string[] | undefined): string[] {
  const out: string[] = [];
  for (const key of keys ?? []) {
    const chord = parseChord(key);
    if (chord && !out.includes(chord)) out.push(chord);
  }
  return out;
}

/** Parse stored prefs, tolerating anything malformed. */
export function parsePrefs(text: string | null): ShortcutPrefs {
  if (!text) return emptyPrefs();
  try {
    const raw = JSON.parse(text) as Partial<ShortcutPrefs>;
    const prefs = emptyPrefs();
    if (typeof raw.profile === 'string') prefs.profile = raw.profile;
    if (raw.layers && typeof raw.layers === 'object') {
      for (const [profile, layer] of Object.entries(raw.layers)) {
        if (!layer || typeof layer !== 'object') continue;
        const clean: Record<string, string[]> = {};
        for (const [id, keys] of Object.entries(layer)) if (Array.isArray(keys)) clean[id] = canonical(keys as string[]);
        prefs.layers[profile] = clean;
      }
    }
    if (Array.isArray(raw.custom)) {
      prefs.custom = raw.custom.filter(
        (p): p is { id: string; name: string; base: string } =>
          !!p && typeof p.id === 'string' && typeof p.name === 'string' && typeof p.base === 'string',
      );
    }
    return prefs;
  } catch {
    return emptyPrefs();
  }
}

export class ShortcutRegistry {
  private commands = new Map<string, Command>();
  private listeners = new Set<() => void>();
  private runListeners = new Set<(command: Command) => void>();
  private prefs: ShortcutPrefs;
  private builtins: KeymapProfile[];
  /** Bumped on every change; React reads it through useSyncExternalStore. */
  version = 0;

  constructor(
    builtins: readonly KeymapProfile[],
    private storage: StorageLike | null = null,
  ) {
    this.builtins = builtins.map((p) => ({ ...p, bindings: normalizeBindings(p.bindings) }));
    this.prefs = parsePrefs(storage?.getItem(STORAGE_KEY) ?? null);
    if (!this.profileById(this.prefs.profile)) this.prefs.profile = 'default';
  }

  /* ------------------------------ commands ------------------------------ */

  /**
   * Add commands. Re-registering an id replaces it (the newest owner wins);
   * the returned function removes exactly what this call added.
   */
  register(specs: CommandSpec | readonly CommandSpec[]): () => void {
    const list = Array.isArray(specs) ? (specs as readonly CommandSpec[]) : [specs as CommandSpec];
    const added: Command[] = [];
    for (const spec of list) {
      const command: Command = { ...spec, context: spec.context ?? GLOBAL, keys: canonical(spec.keys) };
      this.commands.set(command.id, command);
      added.push(command);
    }
    this.changed();
    return () => {
      let removed = false;
      for (const command of added) {
        if (this.commands.get(command.id) === command) {
          this.commands.delete(command.id);
          removed = true;
        }
      }
      if (removed) this.changed();
    };
  }

  get(id: string): Command | undefined {
    return this.commands.get(id);
  }

  list(): Command[] {
    return [...this.commands.values()];
  }

  /** Run a command by id; false when it is unknown, external or disabled. */
  run(id: string): boolean {
    const command = this.commands.get(id);
    if (!command || command.external || !command.run) return false;
    if (command.enabled && !command.enabled()) return false;
    void command.run();
    for (const listener of [...this.runListeners]) listener(command);
    return true;
  }

  /** Observe every command that runs (recent commands, repeat last). */
  onRun(listener: (command: Command) => void): () => void {
    this.runListeners.add(listener);
    return () => this.runListeners.delete(listener);
  }

  /* ------------------------------ bindings ------------------------------ */

  /** Keys from the command defaults and the active profile, before user overrides. */
  profileKeys(id: string, profileId = this.prefs.profile): readonly string[] {
    const command = this.commands.get(id);
    for (const profile of this.profileChain(profileId)) {
      const keys = profile.bindings[id];
      if (keys) return keys;
    }
    return command?.keys ?? [];
  }

  /** The chords that currently trigger a command. */
  keysFor(id: string): readonly string[] {
    const command = this.commands.get(id);
    if (command?.external) return command.keys;
    const override = this.prefs.layers[this.prefs.profile]?.[id];
    return override ?? this.profileKeys(id);
  }

  /** First chord of a command, for hints ("" when unbound). */
  primaryKey(id: string): string {
    return this.keysFor(id)[0] ?? '';
  }

  isOverridden(id: string): boolean {
    return this.prefs.layers[this.prefs.profile]?.[id] !== undefined;
  }

  /** Rebind a command in the active profile. Invalid chords are dropped. */
  setKeys(id: string, keys: readonly string[]): void {
    const command = this.commands.get(id);
    if (command?.external) return;
    const clean = canonical(keys);
    const layer = (this.prefs.layers[this.prefs.profile] ??= {});
    const base = this.profileKeys(id);
    if (sameKeys(clean, base)) delete layer[id];
    else layer[id] = clean;
    this.save();
  }

  resetKeys(id: string): void {
    const layer = this.prefs.layers[this.prefs.profile];
    if (!layer || !(id in layer)) return;
    delete layer[id];
    this.save();
  }

  resetAll(): void {
    delete this.prefs.layers[this.prefs.profile];
    this.save();
  }

  /* ------------------------------ profiles ------------------------------ */

  profiles(): KeymapProfile[] {
    const custom = this.prefs.custom.map<KeymapProfile>((p) => ({
      id: p.id,
      name: p.name,
      builtin: false,
      base: p.base,
      bindings: {},
    }));
    return [...this.builtins, ...custom];
  }

  get activeProfile(): string {
    return this.prefs.profile;
  }

  setProfile(id: string): void {
    if (!this.profileById(id) || id === this.prefs.profile) return;
    this.prefs.profile = id;
    this.save();
  }

  /** New custom profile that starts as a copy of the active one (overrides included). */
  createProfile(name: string): string {
    const base = this.prefs.profile;
    let id = `custom-${slug(name) || 'profile'}`;
    for (let n = 2; this.profileById(id); n++) id = `custom-${slug(name) || 'profile'}-${n}`;
    const baseProfile = this.profileById(base);
    // Custom profiles derive from a built-in and start with the current overrides.
    const builtinBase = baseProfile?.builtin ? base : (baseProfile?.base ?? 'default');
    const layer: Record<string, string[]> = structuredClone(this.prefs.layers[base] ?? {});
    this.prefs.custom.push({ id, name: name.trim() || 'Custom', base: builtinBase });
    this.prefs.layers[id] = layer;
    this.prefs.profile = id;
    this.save();
    return id;
  }

  renameProfile(id: string, name: string): void {
    const profile = this.prefs.custom.find((p) => p.id === id);
    if (!profile || !name.trim()) return;
    profile.name = name.trim();
    this.save();
  }

  deleteProfile(id: string): void {
    const index = this.prefs.custom.findIndex((p) => p.id === id);
    if (index < 0) return;
    const [removed] = this.prefs.custom.splice(index, 1);
    delete this.prefs.layers[id];
    if (this.prefs.profile === id) this.prefs.profile = removed?.base ?? 'default';
    this.save();
  }

  /** The active profile as a portable JSON document (its effective overrides). */
  exportProfile(): string {
    const profile = this.profileById(this.prefs.profile);
    const builtin = profile?.builtin ? profile.id : (profile?.base ?? 'default');
    return JSON.stringify(
      {
        kind: 'ai-studio.keymap',
        version: 1,
        name: profile?.name ?? 'Keymap',
        base: builtin,
        bindings: this.prefs.layers[this.prefs.profile] ?? {},
      },
      null,
      2,
    );
  }

  /** Import an exported keymap as a new custom profile; returns its id or null. */
  importProfile(text: string): string | null {
    try {
      const doc = JSON.parse(text) as { kind?: string; name?: string; base?: string; bindings?: Record<string, string[]> };
      if (doc.kind !== 'ai-studio.keymap' || !doc.bindings) return null;
      const base = this.builtins.some((p) => p.id === doc.base) ? doc.base! : 'default';
      this.prefs.profile = base;
      const id = this.createProfile(doc.name ?? 'Imported');
      const layer: Record<string, string[]> = {};
      for (const [cmd, keys] of Object.entries(doc.bindings)) if (Array.isArray(keys)) layer[cmd] = canonical(keys);
      this.prefs.layers[id] = layer;
      this.save();
      return id;
    } catch {
      return null;
    }
  }

  /* ------------------------------ conflicts ----------------------------- */

  /** Same chord bound twice in one context. */
  conflicts(): Conflict[] {
    const byKey = new Map<string, string[]>();
    for (const command of this.commands.values()) {
      for (const chord of this.keysFor(command.id)) {
        const key = `${command.context}\u0000${chord}`;
        const ids = byKey.get(key) ?? [];
        ids.push(command.id);
        byKey.set(key, ids);
      }
    }
    const out: Conflict[] = [];
    for (const [key, ids] of byKey) {
      if (ids.length < 2) continue;
      const [context, chord] = key.split('\u0000') as [string, string];
      out.push({ chord, context, ids: ids.sort() });
    }
    return out.sort((a, b) => a.chord.localeCompare(b.chord));
  }

  /** Bindings hidden by the same chord in a deeper context (expected, but worth showing). */
  shadows(): Shadow[] {
    const out: Shadow[] = [];
    const all = this.list();
    for (const deep of all) {
      for (const chord of this.keysFor(deep.id)) {
        for (const shallow of all) {
          if (shallow === deep || shallow.context === deep.context) continue;
          if (!contextIncludes(shallow.context, deep.context)) continue;
          if (this.keysFor(shallow.id).includes(chord))
            out.push({ chord, winner: deep.id, hidden: shallow.id, context: deep.context });
        }
      }
    }
    return out;
  }

  /** Other commands a chord would collide with if `id` were bound to it. */
  clashes(id: string, chord: string): { conflicts: string[]; shadows: string[] } {
    const command = this.commands.get(id);
    const context = command?.context ?? GLOBAL;
    const conflicts: string[] = [];
    const shadows: string[] = [];
    for (const other of this.commands.values()) {
      if (other.id === id || !this.keysFor(other.id).includes(chord)) continue;
      if (other.context === context) conflicts.push(other.id);
      else if (contextIncludes(other.context, context) || contextIncludes(context, other.context)) shadows.push(other.id);
    }
    return { conflicts, shadows };
  }

  /** Commands bound to `chord` in the active contexts, most specific first. */
  match(chord: string, activeContexts: readonly string[]): Command[] {
    const out: Command[] = [];
    for (const command of this.commands.values()) {
      if (!activeContexts.includes(command.context)) continue;
      if (this.keysFor(command.id).includes(chord)) out.push(command);
    }
    return out.sort((a, b) => contextDepth(b.context) - contextDepth(a.context));
  }

  /* ------------------------------ plumbing ------------------------------ */

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Re-read storage (another window changed it). */
  reload(): void {
    this.prefs = parsePrefs(this.storage?.getItem(STORAGE_KEY) ?? null);
    if (!this.profileById(this.prefs.profile)) this.prefs.profile = 'default';
    this.changed();
  }

  private profileById(id: string): KeymapProfile | undefined {
    return this.profiles().find((p) => p.id === id);
  }

  /** The profile and the built-in it derives from, nearest first. */
  private profileChain(id: string): KeymapProfile[] {
    const chain: KeymapProfile[] = [];
    let profile = this.profileById(id);
    while (profile && !chain.includes(profile)) {
      chain.push(profile);
      profile = profile.base ? this.profileById(profile.base) : undefined;
    }
    return chain;
  }

  private save(): void {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.prefs));
    } catch {
      // Storage full or unavailable: the change still applies for this session.
    }
    this.changed();
  }

  private changed(): void {
    this.version++;
    for (const listener of [...this.listeners]) listener();
  }
}

function normalizeBindings(bindings: Readonly<Record<string, readonly string[]>>): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [id, keys] of Object.entries(bindings)) out[id] = canonical(keys);
  return out;
}

function sameKeys(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((k, i) => k === b[i]);
}

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
